// VideoGrid Component v1.0.0 - Multi-channel grid/PIP layout
import { useMemo, useCallback } from 'react'
import { useViewerStore } from '../stores/viewerStore'
import VideoPlayer from './VideoPlayer'
import type { CameraChannel, VideoClip } from '../types'

interface VideoGridProps {
  clips: Map<CameraChannel, VideoClip | null>
  onClipEnded?: (channel: CameraChannel) => void
  onTimeUpdate?: (channel: CameraChannel, time: number) => void
}

export default function VideoGrid({ clips, onClipEnded, onTimeUpdate }: VideoGridProps) {
  const {
    selectedChannels,
    layout,
    primaryChannel,
    setPrimaryChannel,
  } = useViewerStore()

  // Grid layout calculation
  const gridConfig = useMemo(() => {
    const count = selectedChannels.length
    if (count === 1) return { cols: 1, rows: 1 }
    if (count === 2) return { cols: 2, rows: 1 }
    if (count <= 4) return { cols: 2, rows: 2 }
    if (count <= 6) return { cols: 3, rows: 2 }
    return { cols: 4, rows: 2 }
  }, [selectedChannels.length])

  const handleChannelClick = useCallback(
    (channel: CameraChannel) => {
      if (layout === 'single') return
      setPrimaryChannel(channel)
    },
    [layout, setPrimaryChannel]
  )

  const handleTimeUpdate = useCallback(
    (channel: CameraChannel) => (time: number) => {
      onTimeUpdate?.(channel, time)
    },
    [onTimeUpdate]
  )

  const handleEnded = useCallback(
    (channel: CameraChannel) => () => {
      onClipEnded?.(channel)
    },
    [onClipEnded]
  )

  // Render based on layout mode
  if (layout === 'single') {
    const clip = clips.get(primaryChannel) || null
    return (
      <div className="flex-1 bg-black">
        <VideoPlayer
          channel={primaryChannel}
          clip={clip}
          isPrimary
          onTimeUpdate={handleTimeUpdate(primaryChannel)}
          onEnded={handleEnded(primaryChannel)}
        />
      </div>
    )
  }

  if (layout === 'pip') {
    // Picture-in-picture: Primary large, others small in corner
    const primaryClip = clips.get(primaryChannel) || null
    const secondaryChannels = selectedChannels.filter((c) => c !== primaryChannel)

    return (
      <div className="flex-1 relative bg-black">
        {/* Primary (full size) */}
        <VideoPlayer
          channel={primaryChannel}
          clip={primaryClip}
          isPrimary
          onTimeUpdate={handleTimeUpdate(primaryChannel)}
          onEnded={handleEnded(primaryChannel)}
        />

        {/* Secondary (PIP thumbnails in bottom-right) */}
        <div className="absolute bottom-4 right-4 flex gap-2">
          {secondaryChannels.map((channel) => {
            const clip = clips.get(channel) || null
            return (
              <div
                key={channel}
                className="w-48 h-32 rounded-lg overflow-hidden shadow-lg cursor-pointer hover:ring-2 hover:ring-viewer-accent transition-all"
                onClick={() => handleChannelClick(channel)}
              >
                <VideoPlayer
                  channel={channel}
                  clip={clip}
                  onTimeUpdate={handleTimeUpdate(channel)}
                  onEnded={handleEnded(channel)}
                />
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // Grid layout (default)
  return (
    <div
      className="flex-1 grid gap-1 bg-viewer-bg p-1"
      style={{
        gridTemplateColumns: `repeat(${gridConfig.cols}, 1fr)`,
        gridTemplateRows: `repeat(${gridConfig.rows}, 1fr)`,
      }}
    >
      {selectedChannels.map((channel) => {
        const clip = clips.get(channel) || null
        const isPrimary = channel === primaryChannel

        return (
          <div
            key={channel}
            className="relative rounded overflow-hidden cursor-pointer hover:ring-2 hover:ring-viewer-accent/50 transition-all"
            onClick={() => handleChannelClick(channel)}
          >
            <VideoPlayer
              channel={channel}
              clip={clip}
              isPrimary={isPrimary}
              onTimeUpdate={handleTimeUpdate(channel)}
              onEnded={handleEnded(channel)}
              onClick={() => handleChannelClick(channel)}
            />
          </div>
        )
      })}
    </div>
  )
}
